'use client';

import { useCallback, useState } from 'react';

import type { DocumentUpdateInput, EditableDocument } from '../types';

interface UseDocumentEditOptions {
  onUpdate: (documentId: string, data: DocumentUpdateInput) => Promise<void>;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseDocumentEditReturn<T extends EditableDocument> {
  editingDocument: T | null;
  isEditing: boolean;
  isPending: boolean;
  editTitle: string;
  editDescription: string;
  setEditTitle: (title: string) => void;
  setEditDescription: (description: string) => void;
  openEdit: (document: T) => void;
  closeEdit: () => void;
  saveEdit: () => Promise<void>;
  canSave: boolean;
}

/**
 * Hook for managing document edit state and mutations
 *
 * @example
 * ```tsx
 * const {
 *   editingDocument,
 *   isEditing,
 *   editTitle,
 *   setEditTitle,
 *   openEdit,
 *   closeEdit,
 *   saveEdit,
 * } = useDocumentEdit({
 *   onUpdate: async (id, data) => {
 *     // Caller supplies the owner-specific update mutation (e.g. a venue or
 *     // production document action) that persists { title, description }.
 *     await persistDocumentUpdate(id, data);
 *   },
 * });
 * ```
 */
export function useDocumentEdit<T extends EditableDocument>({
  onUpdate,
  onSuccess,
  onError,
}: UseDocumentEditOptions): UseDocumentEditReturn<T> {
  const [editingDocument, setEditingDocument] = useState<T | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const openEdit = useCallback((document: T) => {
    setEditingDocument(document);
    setEditTitle(document.title);
    setEditDescription(document.description || '');
  }, []);

  const closeEdit = useCallback(() => {
    setEditingDocument(null);
    setEditTitle('');
    setEditDescription('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingDocument || !editTitle.trim()) return;

    setIsPending(true);
    try {
      await onUpdate(editingDocument.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || undefined,
      });
      onSuccess?.();
      closeEdit();
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Update failed'));
    } finally {
      setIsPending(false);
    }
  }, [
    editingDocument,
    editTitle,
    editDescription,
    onUpdate,
    onSuccess,
    onError,
    closeEdit,
  ]);

  return {
    editingDocument,
    isEditing: editingDocument !== null,
    isPending,
    editTitle,
    editDescription,
    setEditTitle,
    setEditDescription,
    openEdit,
    closeEdit,
    saveEdit,
    canSave: editTitle.trim().length > 0 && !isPending,
  };
}
